import { useClient } from '@reflag/react-sdk';
import * as React from 'react';

import { BLOCK_IF, IF_BLOCK_CONTENT_BLOCKS, ifBlock } from '@gitbook/doc-core';
import type { PaletteGroupDef } from '@gitbook/react-uikit';
import { BlockRange, isBlockRangeSelection } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { capitalizeFirstLetter } from '../../../utils';
import { useSpaceSiteAdaptiveContext } from '../../AdaptiveContent';
import { ContentEditorContext } from '../contexts';

/**
 * Return a palette group definition for turning a block (or range of blocks) into conditional content block.
 */
export function useBlockIfPaletteGroup(input: { nodeKey: string }): () => PaletteGroupDef | null {
    const { nodeKey } = input;
    const editor = useEditor();
    const reflag = useClient();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);

    const siteAdaptiveContext = useSpaceSiteAdaptiveContext();

    return useEventCallback(() => {
        const ifBlockFeature = reflag?.getFlag('IF_BLOCK');
        if (!ifBlockFeature?.isEnabled) {
            return null;
        }

        if (
            siteAdaptiveContext.isUpgradeRequired ||
            !siteAdaptiveContext.site?.adaptiveContent?.enabled
        ) {
            return null;
        }

        const block = editor.value.document.assertBlock(nodeKey);
        const blockRange = isBlockRangeSelection(editor.value.selection)
            ? editor.value.selection
            : BlockRange.createAtBlock(block);

        const blocks = blockRange.getBlocks(editor.value.document);
        const hasOnlyOneIfBlock = blocks.size === 1 && blocks.first()?.type === BLOCK_IF;
        const unsupportedBlockTypes = blocks
            .filter((block) => !IF_BLOCK_CONTENT_BLOCKS.includes(block.type))
            .map((block) => block.type)
            .toArray();

        if (hasOnlyOneIfBlock) {
            return null;
        }

        const turnIntoConditionalContent = () => {
            editor.change((change) => {
                logAnalyticsEvent({
                    type: 'edit_page_document_insert_block',
                    from: 'palette',
                    blockType: BLOCK_IF,
                });
                change.select(blockRange);
                change.insertBlock(ifBlock.createIfBlock('', blocks.toArray()));
            });
        };

        return {
            key: 'conditional-content-commands',
            commands: [
                {
                    key: 'conditional-content',
                    title: 'Turn into conditional content',
                    icon: Icon.Condition,
                    trigger: turnIntoConditionalContent,
                    disabled: unsupportedBlockTypes.length > 0,
                    subtitle:
                        unsupportedBlockTypes.length > 0
                            ? `${unsupportedBlockTypes
                                  .map((type) => capitalizeFirstLetter(type))
                                  .join(', ')} not supported.`
                            : undefined,
                },
            ],
        };
    });
}
